class Carousel {
    constructor(element) {
        this.element = element;
        this.items = element.querySelectorAll('.carousel-item');
        this.controls = element.querySelectorAll('.carousel-control');
        this.indicatorsContainer = element.querySelector('.carousel-indicators');
        this.currentIndex = 0;
        this.isAnimating = false;
        this.autoplayInterval = null;
        this.autoplayDelay = 5000; // 5 seconds
        this.touchStartX = 0;
        this.touchEndX = 0;

        // Add carousel-image class to all images in carousel items
        this.images = Array.from(this.items).map(item => {
            const img = item.querySelector('img');
            img.classList.add('carousel-image');
            return img.src;
        });

        this.init();
    }

    init() {
        this.generateIndicators();
        this.setupEventListeners();
        this.startAutoplay();
        this.update();
    }

    generateIndicators() {
        this.indicatorsContainer.innerHTML = '';
        this.items.forEach((_, index) => {
            const indicator = document.createElement('button');
            indicator.classList.add('carousel-indicator');
            indicator.setAttribute('aria-label', `Slide ${index + 1}`);
            indicator.setAttribute('role', 'tab');
            indicator.setAttribute('aria-controls', this.element.id);
            if (index === 0) indicator.classList.add('active');
            indicator.dataset.slideTo = index;
            this.indicatorsContainer.appendChild(indicator);
        });
    }

    setupEventListeners() {
        // Control buttons (prev/next)
        this.controls.forEach(control => {
            control.addEventListener('click', () => {
                if (!this.isAnimating) {
                    const direction = control.dataset.carouselControl;
                    this.navigate(direction === 'prev' ? -1 : 1);
                    this.resetAutoplay();
                }
            });
            control.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    control.click();
                }
            });
        });

        // Indicators
        this.indicatorsContainer.querySelectorAll('.carousel-indicator').forEach((indicator, index) => {
            indicator.addEventListener('click', () => {
                if (!this.isAnimating) {
                    this.goToSlide(index);
                    this.resetAutoplay();
                }
            });
            indicator.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.goToSlide(index);
                    this.resetAutoplay();
                }
            });
        });

        // Image click for fullscreen
        this.items.forEach(item => {
            const image = item.querySelector('.carousel-image');
            image.addEventListener('click', () => {
                this.openFullscreen(image.src);
                this.pauseAutoplay();
            });
        });

        // Touch/swipe events for regular carousel
        this.element.addEventListener('touchstart', (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
            this.pauseAutoplay();
        });
        this.element.addEventListener('touchend', (e) => {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleSwipe();
            this.resetAutoplay();
        });

        // Pause autoplay on hover/focus
        this.element.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.element.addEventListener('mouseleave', () => this.resetAutoplay());
        this.element.addEventListener('focusin', () => this.pauseAutoplay());
        this.element.addEventListener('focusout', () => this.resetAutoplay());
    }

    handleSwipe() {
        const swipeThreshold = 50; // Minimum swipe distance in pixels
        const swipeDistance = this.touchEndX - this.touchStartX;
        if (swipeDistance > swipeThreshold) {
            this.navigate(-1); // Swipe right -> previous
        } else if (swipeDistance < -swipeThreshold) {
            this.navigate(1); // Swipe left -> next
        }
    }

    handleFullscreenSwipe(direction) {
        const swipeThreshold = 50; // Minimum swipe distance in pixels
        const swipeDistance = this.touchEndX - this.touchStartX;
        if (swipeDistance > swipeThreshold) {
            direction(-1); // Swipe right -> previous
        } else if (swipeDistance < -swipeThreshold) {
            direction(1); // Swipe left -> next
        }
    }

    startAutoplay() {
        this.autoplayInterval = setInterval(() => {
            this.navigate(1);
        }, this.autoplayDelay);
    }

    pauseAutoplay() {
        clearInterval(this.autoplayInterval);
    }

    resetAutoplay() {
        this.pauseAutoplay();
        this.startAutoplay();
    }

    navigate(direction) {
        if (this.isAnimating) return;
        const prevIndex = this.currentIndex;
        this.currentIndex = (this.currentIndex + direction + this.items.length) % this.items.length;
        this.update(prevIndex, direction);
    }

    goToSlide(index) {
        if (this.isAnimating || index === this.currentIndex) return;
        const prevIndex = this.currentIndex;
        const direction = index > prevIndex ? 1 : -1;
        this.currentIndex = index;
        this.update(prevIndex, direction);
    }

    update(prevIndex, direction = 1) {
        this.isAnimating = true;

        // Remove active and prev-slide classes from all items
        this.items.forEach((item, index) => {
            item.classList.remove('active', 'prev-slide');
        });

        // Set the previous slide with prev-slide class for animation
        if (prevIndex !== undefined) {
            if (direction > 0) {
                this.items[prevIndex].classList.add('prev-slide');
            }
        }

        // Set the active slide
        this.items[this.currentIndex].classList.add('active');

        // Update indicators
        this.indicatorsContainer.querySelectorAll('.carousel-indicator').forEach((indicator, index) => {
            indicator.classList.toggle('active', index === this.currentIndex);
            indicator.setAttribute('aria-selected', index === this.currentIndex ? 'true' : 'false');
        });

        // Reset animation state after transition
        setTimeout(() => {
            this.isAnimating = false;
            this.items.forEach(item => item.classList.remove('prev-slide'));
        }, 500); // Match CSS transition duration
    }

    openFullscreen(src) {
        const initialIndex = this.images.indexOf(src);
        let currentIndex = initialIndex;

        const overlay = document.createElement('div');
        overlay.classList.add('fullscreen-overlay');
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-label', 'Fullscreen image viewer');

        // Create a container for fullscreen items
        const fullscreenInner = document.createElement('div');
        fullscreenInner.classList.add('fullscreen-inner');

        // Create fullscreen items for each image
        this.images.forEach((imageSrc, index) => {
            const fullscreenItem = document.createElement('div');
            fullscreenItem.classList.add('fullscreen-item');
            if (index === currentIndex) {
                fullscreenItem.classList.add('active');
            }
            fullscreenItem.innerHTML = `<img src="${imageSrc}" alt="Fullscreen image ${index + 1}">`;
            fullscreenInner.appendChild(fullscreenItem);
        });

        overlay.appendChild(fullscreenInner);
        overlay.innerHTML += `
      <button class="close-button" aria-label="Close fullscreen">×</button>
      <button class="fullscreen-control prev" aria-label="Previous image">←</button>
      <button class="fullscreen-control next" aria-label="Next image">→</button>
      <div class="image-counter">${currentIndex + 1}/${this.images.length}</div>
    `;
        document.body.appendChild(overlay);

        const fullscreenItems = overlay.querySelectorAll('.fullscreen-item');
        const counterElement = overlay.querySelector('.image-counter');
        const prevButton = overlay.querySelector('.fullscreen-control.prev');
        const nextButton = overlay.querySelector('.fullscreen-control.next');
        const closeButton = overlay.querySelector('.close-button');

        const updateFullscreenImage = (prevIndex, direction = 1) => {
            fullscreenItems.forEach((item, index) => {
                item.classList.remove('active', 'prev-slide');
            });

            if (prevIndex !== undefined && prevIndex >= 0 && prevIndex < this.images.length) {
                if (direction > 0) {
                    fullscreenItems[prevIndex].classList.add('prev-slide');
                }
            }

            fullscreenItems[currentIndex].classList.add('active');
            counterElement.textContent = `${currentIndex + 1}/${this.images.length}`;
        };

        const navigateFullscreen = (direction) => {
            if (this.isAnimating) return;
            this.isAnimating = true;
            const prevIndex = currentIndex;
            currentIndex = (currentIndex + direction + this.images.length) % this.images.length;
            updateFullscreenImage(prevIndex, direction);
            setTimeout(() => {
                this.isAnimating = false;
                fullscreenItems.forEach(item => item.classList.remove('prev-slide'));
            }, 500); // Match CSS transition duration
        };

        prevButton.addEventListener('click', () => navigateFullscreen(-1));
        nextButton.addEventListener('click', () => navigateFullscreen(1));

        // Touch/swipe events for fullscreen overlay
        overlay.addEventListener('touchstart', (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
        });
        overlay.addEventListener('touchend', (e) => {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleFullscreenSwipe(navigateFullscreen);
        });

        const closeOverlay = () => {
            overlay.classList.remove('active');
            setTimeout(() => {
                overlay.remove();
                document.removeEventListener('keydown', keyHandler);
                this.resetAutoplay();
            }, 700); // Match CSS transition duration
        };

        const keyHandler = (e) => {
            if (e.key === 'ArrowLeft') navigateFullscreen(-1);
            if (e.key === 'ArrowRight') navigateFullscreen(1);
            if (e.key === 'Escape') closeOverlay();
        };

        document.addEventListener('keydown', keyHandler);

        closeButton.addEventListener('click', closeOverlay);

        overlay.addEventListener('click', (e) => {
            // Close if clicking the overlay background, fullscreen-inner, or fullscreen-item (but not the image or controls)
            if (
                e.target === overlay ||
                (e.target.closest('.fullscreen-inner') && !e.target.closest('img') && !e.target.closest('.fullscreen-control') && !e.target.closest('.close-button') && !e.target.closest('.image-counter')) ||
                (e.target.closest('.fullscreen-item') && !e.target.closest('img') && !e.target.closest('.fullscreen-control') && !e.target.closest('.close-button') && !e.target.closest('.image-counter'))
            ) {
                closeOverlay();
            }
        });

        setTimeout(() => overlay.classList.add('active'), 50); // Slight delay for animation
    }
}

// Export initialization function
export function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(carousel => {
        new Carousel(carousel);
    });
}
